import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type { InputSize } from '../input/input';

/** Size variant — matches om-input (re-export for convenience). */
export type ChoiceSegmentsSize = InputSize;

/** Single option rendered inside the choice segments component. */
export interface SegmentOption {
  /** Unique value emitted on selection. */
  value: string;
  /** Visible label text. */
  label: string;
  /** Optional emoji or Material Symbols icon name (e.g. '👦' or 'child_care'). */
  icon?: string;
}

/**
 * Choice Segments: A segmented control for selecting one option from a small set.
 * Styled consistently with om-input for form consistency.
 */
@Component({
  selector: 'om-choice-segments',
  imports: [CommonModule],
  templateUrl: './choice-segments.html',
  styleUrl: './choice-segments.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChoiceSegments {
  /** List of selectable segments. Must contain at least 2 items. */
  options = input.required<SegmentOption[]>();

  /** Currently selected value (two-way bindable via model). */
  value = model.required<string>();

  /** Visual size variant (same as om-input: sm, md, lg). */
  size = input<InputSize>('md');

  /** Index of the currently selected choice. */
  selectedIndex = computed(() => {
    const idx = this.options().findIndex((o) => o.value === this.value());
    return idx >= 0 ? idx : 0;
  });

  /** Width percentage for each choice segment. */
  segmentWidthPct = computed(() => 100 / this.options().length);

  /** CSS left position of the selection indicator (4px gap from container padding). */
  indicatorLeft = computed(() => {
    const idx = this.selectedIndex();
    const pct = this.segmentWidthPct();
    return `calc(${idx * pct}% + 4px)`;
  });

  /** CSS width of the selection indicator (4px gap on each side between segments). */
  indicatorWidth = computed(() => {
    const count = this.options().length;
    return `calc(${100 / count}% - 8px)`;
  });

  /** Container height — total component height matches om-input (52px for md). */
  containerHeight = computed(() => {
    const sizeMap: Record<InputSize, string> = {
      sm: 'h-10',
      md: 'h-[52px]',
      lg: 'h-14',
    };
    return sizeMap[this.size()];
  });

  /** Choice button height and text — content area after p-1 (8px) + border (2px) = 10px. */
  choiceButtonClasses = computed(() => {
    const sizeMap: Record<InputSize, string> = {
      sm: 'h-[30px] text-xs',       /* 40 - 10 */
      md: 'h-[42px] text-sm',       /* 52 - 10 */
      lg: 'h-[46px] text-base',     /* 56 - 10 */
    };
    return sizeMap[this.size()];
  });

  /** Detects if the icon string is an emoji (non-ASCII) vs a Material Symbols name. */
  isEmoji(icon: string): boolean {
    return /[\u{1F000}-\u{1FFFF}]|[\u{2600}-\u{27BF}]|[\u{FE00}-\u{FEFF}]|[\u{1F900}-\u{1F9FF}]|[\u{200D}]|[\u{20E3}]|[\u{E0020}-\u{E007F}]/u.test(
      icon
    );
  }

  /** Selects a segment by value. */
  select(value: string): void {
    this.value.set(value);
  }
}
